import {
  Accidental,
  BarlineType,
  Flow,
  FormatParams,
  Formatter,
  RenderContext,
  Renderer,
  StaveConnector,
  TickContext,
  Voice,
} from "vexflow";
import {Position, TouchListener} from "./touchListener";
import {NoteMap} from "./noteMap";
import {
  checkCurrentNote,
  checkCurrentStave,
  findWhichStaveMouseOver,
  formatStavesToJson,
  getMoveKeysFunc,
  groupSequence,
} from "../utils/helpers";
import {PROVISORY_TICKABLE_STYLE} from "./constants";
import {StaveNote} from "./extension/MusiconStaveNote";
import Stave from "./extension/MusiconStave";
import Player, {ClefType, PlayInfo} from "./player";
import {BarNote} from "./extension/MusiconBarNote";
import {
  checkParallel,
  findPreviousTwoNote,
  generate358IntervalNote,
  generate58IntervalNote,
  generateStepNote,
  handleHiddenOctaveFifth,
  handleJumping,
  handleParallelFifth,
  handleParallelOctave,
  noteTo12,
  staveDataTransfer
} from "../utils/noteTransfer";
import {distance, Interval, Note, transpose} from "tonal";

const CANVAS_HEIGHT = 260;
const LEFT_PADDING = 60;
const STAVE_WIDTH = 150;
const HEAD_STAVE_WIDTH = 75;
const MELODY_TOP = 20;
const CHORD_TOP = 100 + MELODY_TOP;

const MELODY_KEY_RANGE = { max: "G/6", min: "B/2" };
const CHORD_KEY_RANGE = { max: "A/5", min: "B/2" };

export const SCORE_STORE_KEY = "local-music-score";

export class Handler {
  static instance: Handler;
  private render: Renderer;
  private ctx: RenderContext;
  private staveList: Stave[] = [];
  private melodyNoteList: StaveNote[] = [];
  private chordNoteList: StaveNote[] = [];
  private title: string = "";

  private melodyHeadStave = new Stave(
    LEFT_PADDING,
    MELODY_TOP,
    HEAD_STAVE_WIDTH
  );
  private melodyStaveList: Stave[] = [];

  private chordHeadStave = new Stave(LEFT_PADDING, CHORD_TOP, HEAD_STAVE_WIDTH);
  private chordStaveList: Stave[] = [];

  public currentStave: Stave | null = null;
  public currentNote: StaveNote | null = null;

  private provisoryTickable: StaveNote | null = null;
  public duration: string = "4";

  private preProvisoryTickable: StaveNote | null = null;

  private dragCursorX: number | null = null;
  private currentNoteCursorX: number | null = null;

  private noteUpStatus: number = 0;
  public afterInsertNoteUpStatusCallback: () => void = () => {};

  private noteDownStatus: number = 0;
  public afterInsertNoteDownStatusCallback: () => void = () => {};

  private player: Player;
  private chordPlayer: Player;

  private currentBarIndex: number | undefined = undefined
  // i.e {bar_0: 4, bar_1: 2, bar_2: 1}
  private melodyNumber: {[key: string]: number} = {}
  private chordNumber: {[key: string]: number} = {}

  public playing: boolean = false;

  public static getInstance(render: Renderer): Handler {
    if (!Handler.instance) Handler.instance = new Handler(render);
    return Handler.instance;
  }

  private constructor(render: Renderer) {
    this.render = render;
    this.ctx = render.getContext();
    this.player = new Player(this);
    this.chordPlayer = new Player(this);
  }

  public init = (data?: any) => {
    this.restoreScore(data);
    this.redraw();
  };

  public getContext = (): RenderContext => this.ctx;
  public getStaveList = (): Stave[] => this.staveList;
  public getNoteList = (): StaveNote[] => this.melodyNoteList;
  public getMelodyStaveList = (): Stave[] => this.melodyStaveList;
  public getChordStaveList = (): Stave[] => this.chordStaveList;

  public getCurrentStave = (): Stave | null => this.currentStave;
  public setCurrentStave = (stave: Stave | null) => {
    this.currentStave = stave;

    if (stave == null) {
      this.dragCursorX = null;
    }
  };

  public getCurrentNote = (): StaveNote | null => this.currentNote;
  public setCurrentNote = (note: StaveNote | null) => {
    this.currentNote = note;

    try {
      this.currentNoteCursorX = note?.getAbsoluteX() ?? null;
    } catch (e) {}
  };

  public getDragCursorX = (): number | null => this.dragCursorX;
  public setDragCursorX = (x: number | null) => {
    this.dragCursorX = x;
  };

  public getProvisoryTickable = (): StaveNote | null =>
    this.preProvisoryTickable;

  public setProvisoryTickable = (note: StaveNote | null) => {
    this.preProvisoryTickable = this.provisoryTickable;
    this.provisoryTickable = note;
  };

  public getNoteUpStatus = (): number => this.noteUpStatus;
  public setNoteUpStatus = (status: number) => {
    this.noteUpStatus = status;
  };

  public getNoteDownStatus = (): number => this.noteDownStatus;
  public setNoteDownStatus = (status: number) => {
    this.noteDownStatus = status;
  };

  public getScoreData = (): string => {
    const scoreData = localStorage.getItem(SCORE_STORE_KEY);

    if (!scoreData) {
      return "";
    }

    return scoreData;
  };

  public addEmptyStaves = (num: number = 1) => {
    for (let i = 0; i < num; i++) {
      const [newMelodyStave, newChordStave] = this.getEmptyStave();

      this.melodyStaveList.push(newMelodyStave);
      this.chordStaveList.push(newChordStave);
    }

    this.staveList = [...this.melodyStaveList, ...this.chordStaveList];
  };

  public redraw = () => {
    this.ctx.clear();
    this._drawHeads();
    this._drawDragCursor();
    this._drawNoteCursor();
    this._drawStaves();
    this._drawNotes();
    this._drawProvisoryTickable();
  };

  public startListenTouchEvent(canvas: HTMLCanvasElement | null) {
    if (!canvas) return;
    const listener = TouchListener.getInstance(canvas, this.render);
    listener.startListening();
  }

  public stopListenTouchEvent = (canvas: HTMLCanvasElement | null) => {
    if (!canvas) return;
    const listener = TouchListener.getInstance(canvas, this.render);
    listener.stopListening();
  };

  public getNextStave = (stave: Stave) => {
    const box = stave.getBoundingBox();

    const mousePos = {
      x: box.x + box.w + 1,
      y: stave.getTopLineTopY(),
    };

    const next = findWhichStaveMouseOver(this.staveList, mousePos);

    return next;
  };

  private getEmptyStave = () => {
    let lastMelody = this.melodyHeadStave;

    if (this.melodyStaveList.length > 0) {
      lastMelody = this.melodyStaveList[this.melodyStaveList.length - 1];
    }

    const mbox = lastMelody.getBoundingBox();

    const x = mbox.getX() + mbox.getW();
    const newMelodyStave = new Stave(x, MELODY_TOP, STAVE_WIDTH);
    newMelodyStave.setBegBarType(BarlineType.NONE);

    const newChordStave = new Stave(x, CHORD_TOP, STAVE_WIDTH);
    newChordStave.setBegBarType(BarlineType.NONE);

    return [newMelodyStave, newChordStave];
  };

  private _drawHeads = () => {
    const mhs = new Stave(LEFT_PADDING, MELODY_TOP, 80);
    mhs.addClef("treble");
    mhs.addTimeSignature("4/4");
    mhs.setWidth(75);
    mhs.setEndBarType(7);
    mhs.setContext(this.ctx).draw();
    this.melodyHeadStave = mhs;

    const chs = new Stave(LEFT_PADDING, CHORD_TOP, 80);
    chs.addClef("bass");
    chs.addTimeSignature("4/4");
    chs.setWidth(75);
    chs.setEndBarType(7);
    chs.setContext(this.ctx).draw();
    this.chordHeadStave = chs;

    const BraceConnector = new StaveConnector(
      this.melodyHeadStave,
      this.chordHeadStave
    );
    BraceConnector.setType(StaveConnector.type.BRACE);
    BraceConnector.setContext(this.ctx).draw();

    const singleLeftConnector = new StaveConnector(
      this.melodyHeadStave,
      this.chordHeadStave
    );

    singleLeftConnector.setType(StaveConnector.type.SINGLE_LEFT);
    singleLeftConnector.setContext(this.ctx).draw();
  };

  private _drawStaves = () => {
    this.melodyStaveList.forEach((stave: Stave, i: number) => {
      stave.setContext(this.ctx).draw();

      const rightLineConnector = new StaveConnector(
        stave,
        new Stave(stave.getX(), CHORD_TOP, STAVE_WIDTH)
      );
      rightLineConnector.setType(StaveConnector.type.SINGLE_RIGHT);
      rightLineConnector.setContext(this.ctx).draw();
    });
    this.chordStaveList.forEach((stave: Stave, i: number) => {
      stave.setContext(this.ctx).draw();
    });
  };

  private _drawNotes = (stave?: Stave) => {
    if (stave === undefined) {
      this.staveList.forEach((stave) => {
        this._drawNotes(stave);
      });
    } else {
      const voice = new Voice(Flow.TIME4_4).setMode(Voice.Mode.SOFT);
      const notes = stave.getTickables().map((note: StaveNote) => {
        note.setCenterAlignment(false);
        note.setWidth(25);
        return note;
      });
      voice.addTickables(notes);

      const formatter = new Formatter();
      const formatOption = {
        align_rests: true,
        auto_beam: true,
        width: 50,
        minSpace: 50,
      } as FormatParams;

      formatter.joinVoices([voice]);

      if (stave.getTickables().length > 0) {
        formatter.formatToStave([voice], stave, formatOption);
      }
      voice.draw(this.ctx, stave);
    }
  };

  public updateProvisoryKey = (mousePos: Position) => {
    this.dragCursorX = mousePos.x;
    if (this.currentStave == null) {
      return;
    }

    if (this.provisoryTickable == null) {
      let newNote = new StaveNote({ keys: ["d/4"], duration: this.duration });

      this.setProvisoryTickable(newNote);
    }

    if (
      !(this.provisoryTickable instanceof StaveNote) ||
      this.provisoryTickable?.getNoteType() == "r"
    ) {
      //No need to update key if its not a note or if its a rest, just draw the tickable in the new mouse position
    } else {
      const noteName = NoteMap.getNoteName(this.currentStave, mousePos);

      if (noteName != this.provisoryTickable.getKeys()[0]) {
        const cloneNote = this.provisoryTickable.clone({
          keys: [noteName],
        });
        this.setProvisoryTickable(cloneNote);

        if (this.provisoryTickable) {
          this.provisoryTickable.setStyle(PROVISORY_TICKABLE_STYLE);
        }
        this.provisoryTickable?.autoStem();
        this.provisoryTickable?.setStave(this.currentStave);
        this.provisoryTickable?.setTickContext(new TickContext());
      }
    }
    this.drawProvisoryTickable(mousePos);
  };

  public drawProvisoryTickable = (mousePos: Position) => {
    if (
      this.currentStave == null ||
      this.provisoryTickable == null ||
      !mousePos
    )
      return;

    if (this.provisoryTickable instanceof StaveNote) {
      //Fix X position to set exactly where the mouse is
      //TODO the -5 value shouldnt be absolute! it should reflect half the note's Width
      const xShift = mousePos.x - this.provisoryTickable.getAbsoluteX() - 5;
      this.provisoryTickable.setXShift(xShift);
    }

    this.dragCursorX = mousePos.x;

    this.redraw();
  };

  public isHeadStave = (pos: Position) => {
    return (
      this.melodyHeadStave.getX() + this.melodyHeadStave.getWidth() >= pos.x
    );
  };

  private _drawNoteCursor = () => {
    const x = this.currentNoteCursorX;
    const stave = this.currentStave;

    this.currentBarIndex = undefined

    if (!stave || !x) return;

    const space = stave.getSpacingBetweenLines();
    const y = stave.getTopLineTopY();
    const w = space * 2;
    const h = space * 4;

    const tempFillStyle = this.ctx.fillStyle;

    this.ctx.fillStyle = "#FDE3AF";
    this.ctx.fillRect(x - space / 2, y, w, h);
    // 找到当前选中的小节编号
    if (this.currentStave?.getY() === 20) {
      this.currentBarIndex = (this.currentStave?.getX() - 135) / 150
    }

    this.ctx.fillStyle = tempFillStyle;
  };

  private _drawProvisoryTickable = () => {
    if (!this.currentStave) return;
    if (!this.provisoryTickable) return;

    //Only draw Provisory note if not on a definitive note
    const tempFillStyle = this.ctx.fillStyle;
    const tempStrokeStyle = this.ctx.strokeStyle;
    this.ctx.fillStyle = "gray";
    this.ctx.strokeStyle = "gray";

    this.provisoryTickable.setStave(this.currentStave);
    this.provisoryTickable.draw();

    this.ctx.fillStyle = tempFillStyle;
    this.ctx.strokeStyle = tempStrokeStyle;
  };

  private _drawDragCursor = () => {
    if (!this.dragCursorX || !this.currentStave) {
      return;
    }

    const tempFillStyle = this.ctx.fillStyle;
    this.ctx.fillStyle = "rgba(255, 241, 212, 0.80)";

    const space = this.currentStave.getSpacingBetweenLines();
    const x = this.dragCursorX - space / 2;
    const y = 0;
    const w = space * 2;
    const h = 240;

    this.ctx.fillRect(x, y, w, h);

    this.ctx.fillStyle = tempFillStyle;
  };

  public getLastTickableBeforeXPosition = (x: number) => {
    return this._findNearStaveNote(x, true);
  };

  public getFirstTickableAfterXPosition = (x: number) => {
    return this._findNearStaveNote(x, false);
  };

  private _findNearStaveNote = (x: number, isPre: boolean = true) => {
    const stave = this.currentStave;
    const staveNotes = stave?.getTickables() ?? [];

    if (staveNotes.length == 0) {
      return null;
    }

    let nearNotes;

    if (isPre) {
      nearNotes = staveNotes.filter((n) => n.getAbsoluteX() < x);
      nearNotes.sort((a: StaveNote, b: StaveNote) => {
        return a.getAbsoluteX() - b.getAbsoluteX() < 0 ? 1 : -1;
      });
    } else {
      nearNotes = staveNotes.filter((n) => n.getAbsoluteX() > x);
      nearNotes.sort((a: StaveNote, b: StaveNote) => {
        return a.getAbsoluteX() - b.getAbsoluteX() > 0 ? 1 : -1;
      });
    }

    return nearNotes[0] ?? null;
  };

  public rebuildStaveList = () => {
    this.rebuildMelodyStaves();
    this.rebuildChordStaves();
    this.completeStaves();

    const score = {
      title: this.title,
      melodyStaves: formatStavesToJson(this.melodyStaveList),
      chordStaves: formatStavesToJson(this.chordStaveList),
    };

    localStorage.setItem(SCORE_STORE_KEY, JSON.stringify(score));

    // 重置画布大小
    const width = 300 + this.melodyStaveList.length * STAVE_WIDTH;
    this.render.resize(width, CANVAS_HEIGHT);
  };

  private rebuildMelodyStaves = () => {
    const sortStave = this.melodyStaveList.sort((a, b) => a.getX() - b.getX());

    const notes: StaveNote[] = [];
    sortStave.map((stave: Stave) => {
      stave.getTickables().map((note: StaveNote) => {
        notes.push(note);
      });
    });

    const staveNotes = groupSequence(notes);

    this.melodyNoteList = notes;

    const newStaves = staveNotes.map(this.noteToStave(this.melodyHeadStave));

    this.melodyStaveList = newStaves;
    this.staveList = [...this.melodyStaveList, ...this.chordStaveList];
  };

  private rebuildChordStaves = () => {
    const sortStave = this.chordStaveList.sort((a, b) => a.getX() - b.getX());

    const notes: StaveNote[] = [];
    sortStave.map((stave: Stave) => {
      stave.getTickables().map((note: StaveNote) => {
        notes.push(note);
      });
    });
    this.chordNoteList = notes;
    const staveNotes = groupSequence(notes);

    const newStaves = staveNotes.map(this.noteToStave(this.chordHeadStave));

    this.chordStaveList = newStaves;
    this.staveList = [...this.melodyStaveList, ...this.chordStaveList];
  };

  private completeStaves = () => {
    const melodyGroupNotes = groupSequence(this.melodyNoteList);
    const chordGroupNotes = groupSequence(this.chordNoteList);

    const maxLength = Math.max(melodyGroupNotes.length, chordGroupNotes.length);

    const group = [];

    for (let i = 0; i < maxLength + 5; i++) {
      group.push([]);
    }

    const melodyStaves = group.map(this.noteToStave(this.melodyHeadStave));
    const chordStaves = group.map(this.noteToStave(this.chordHeadStave));

    this.melodyStaveList = [...this.melodyStaveList, ...melodyStaves];
    this.chordStaveList = [...this.chordStaveList, ...chordStaves];

    this.staveList = [...this.melodyStaveList, ...this.chordStaveList];
  };

  private noteToStave =
    (headStave: Stave) => (notes: StaveNote[], i: number) => {
      const x = headStave.getX() + headStave.getWidth() + STAVE_WIDTH * i;
      const y = headStave.getY();
      const w = STAVE_WIDTH;
      const stave = new Stave(x, y, w);
      let preNote: StaveNote | null = null;

      notes.map((note: StaveNote) => {
        stave.insertTickableBetween(note, preNote, null);
        preNote = note;
      });

      stave.setBegBarType(7);

      return stave;
    };

  public cleanAll = () => {
    this.chordStaveList = [];
    this.melodyStaveList = [];
    this.melodyNoteList = [];
    this.staveList = [];
    this.addEmptyStaves(6);
    localStorage.removeItem(SCORE_STORE_KEY);

    this.redraw();
  };

  public upAllKeys = () => {
    this.melodyStaveList = this.melodyStaveList.map(
      getMoveKeysFunc("up", this.currentNote, MELODY_KEY_RANGE)
    );
    this.chordStaveList = this.chordStaveList.map(
      getMoveKeysFunc("up", this.currentNote, CHORD_KEY_RANGE)
    );

    this.staveList = [...this.melodyStaveList, ...this.chordStaveList];
    this.rebuildStaveList();
    this.redraw();
  };

  public downAllKeys = () => {
    this.melodyStaveList = this.melodyStaveList.map(
      getMoveKeysFunc("down", this.currentNote, MELODY_KEY_RANGE)
    );
    this.chordStaveList = this.chordStaveList.map(
      getMoveKeysFunc("down", this.currentNote, CHORD_KEY_RANGE)
    );

    this.staveList = [...this.melodyStaveList, ...this.chordStaveList];
    this.rebuildStaveList();
    this.redraw();
  };

  public deleteNote = () => {
    if (!this.currentNote) {
      this.melodyStaveList = [];
      this.chordStaveList = [];

      this.staveList = [];

      this.currentNoteCursorX = null;
      this.rebuildStaveList();
      this.redraw();

      return;
    }

    const removeNote = (stave: Stave) => {
      if (!checkCurrentStave(stave, this.currentNote)) return stave;

      const newNotes = stave.getTickables().filter((note: StaveNote) => {
        return !checkCurrentNote(note, this.currentNote);
      });

      stave.setTickables(newNotes);

      return stave;
    };

    this.melodyStaveList.map(removeNote);
    this.chordStaveList.map(removeNote);

    this.currentNoteCursorX = null;
    this.rebuildStaveList();
    this.redraw();
  };

  private restoreScore = (data: any) => {
    try {
      if (!data) {
        this.addEmptyStaves(5);
        return;
      }

      this.restoreStaves(data.melodyStaves, this.melodyStaveList);
      this.restoreStaves(data.chordStaves, this.chordStaveList);

      this.rebuildStaveList();
    } catch (e) {
      this.addEmptyStaves(5);
      console.error("解析数据失败", e);
      console.info("解析数据失败数据", data);
    }
  };

  private restoreStaves = (staveData: any[], staves: Stave[]) => {
    if (!Array.isArray(staves) || !Array.isArray(staveData)) {
      return;
    }

    this.addEmptyStaves(staveData.length);

    staveData.map(({ notes }, i) => {
      const stave = staves[i];
      const newNotes = notes.map(
        ({
          keys,
          duration,
          accidentals,
        }: {
          keys: string[];
          duration: string;
          accidentals: string[];
        }) => {
          const note = new StaveNote({ keys, duration });
          note.autoStem();

          accidentals.map((accidental: any): void => {
            if (accidental === null) {
              return;
            }

            if (typeof accidental === "string") {
              note.addModifier(new Accidental(accidental), 0);
              return;
            }

            const index = accidental.index;

            note.addModifier(new Accidental(accidental.modifier), index);
          });

          return note;
        }
      );
      stave.setTickables(newNotes);
    });
  };

  public play = (callback?: () => void) => {
    if (this.playing) return;

    this.playing = true;

    const onFinished = () => {
      this.playing = false;
    };

    const max = Math.max(
      this.melodyStaveList.length,
      this.chordStaveList.length
    );

    this.playStaves(this.player, this.melodyStaveList, "treble", () => {
      if (this.melodyStaveList.length == max) {
        onFinished();
        callback && callback();
      }
    });
    this.playStaves(this.chordPlayer, this.chordStaveList, "bass", () => {
      if (this.chordStaveList.length == max) {
        onFinished();
        callback && callback();
      }
    });
  };

  private playStaves = (
    player: Player,
    staves: Stave[],
    clef: ClefType,
    callback: () => void
  ) => {
    //TODO RPM should be set outside...
    const rpm = 120;
    const playInfo: PlayInfo = {
      delay: 0,
      rpm: rpm,
      defaultTime: rpm / 60, // to seconds
      clef,
    };

    let playEvents: any[] = [];

    staves.map((stave: Stave) => {
      //set clef to playinfo
      playInfo.clef = clef;

      //Call initial barline play events
      const barNote = new BarNote();

      stave.getTickables().map((note: StaveNote) => {
        playEvents = playEvents.concat(note.getPlayEvents(playInfo));
      });

      playEvents = playEvents.concat(
        barNote.getPlayEvents(playInfo, playEvents)
      );
    });

    player.addEvents(playEvents);
    player.onPlayFinished(() => {
      callback();
    });
    player.play();
  };

  public stop = () => {
    this.player.stop();
    this.chordPlayer.stop();
    this.playing = false;
  };

  /**
   *
   * @param melodyNum
   * @param chordNum
   *
   */
  public structMatching = (melodyNum: number = 1, chordNum: number = 1) => {
    // 如果非选中某一个小节或者第一次渲染和弦，就把对应关系付给全局变量
    if (this.currentBarIndex === undefined) {
      Array.from({ length: 1000 }, (_, index) => index).forEach((val: number) => {
        this.melodyNumber[val] = melodyNum
        this.chordNumber[val] = chordNum
      })
    } else { // 否则就把每次选中的对应关系保存到相关的小节中
      this.melodyNumber[this.currentBarIndex] = melodyNum
      this.chordNumber[this.currentBarIndex] = chordNum
    }
    // 先拿到当前的旋律部分音符
    const melodyStaves = formatStavesToJson(this.melodyStaveList)
    let chordStructs: Array<any> = []
    let baseNotes: Array<any> = []
    let relations: Array<any> = []
    const chordDurations: Array<any> = []
    // console.log(distance('E4', 'G2'))
    // console.log(transpose('Bb4', "P5"))
    melodyStaves.forEach((stave, i) => {
      // i是第几小节
      chordStructs[i] = []
      baseNotes[i] = []
      const notes = stave.notes
      const currentBarMelodyNum = this.melodyNumber[i] !== undefined ? this.melodyNumber[i] : melodyNum
      notes.forEach((note, n) => {
        // 当前音符
        const key = note.keys[0].split("/")[0];
        const octave = parseInt(note.keys[0].split("/")[1]);
        const accidentals = note.accidentals[0] ? note.accidentals[0].modifier : "";
        const baseDuration = note.duration;
        let chordDuration = "4"
        const baseNote = `${key}${accidentals}${octave}`;
        baseNotes[i].push(baseNote)
        // console.log(Note.transpose(baseNote, '-13m'))
        // 根据旋律和和弦的对应关系，找到需要搭配骨架的旋律音
        if (currentBarMelodyNum === 1) { chordDuration = baseDuration }
        if (currentBarMelodyNum === 2) {
          if (baseDuration === '1') {chordDuration = '1'}
          if (baseDuration === '2') {chordDuration = '1'}
          if (baseDuration === '4') {chordDuration = '2'}
          // 二对一
          if (baseDuration === '2' && n === 1) {
            // 二分音符只给第一个配和弦
            return
          }
          if (baseDuration === '4' && (n === 1 || n === 3)) {
            // 四分音符只给第一个和第三个配和弦
            return
          }
        }
        if (currentBarMelodyNum === 4) {
          chordDuration = '1'
          // 四对一
          if (baseDuration === '2' && n === 1) {
            // 二分音符只给第一个配和弦
            return
          }
          if (baseDuration === '4' && n !== 0) {
            // 四分音符只给第一个配和弦
            return
          }
        }
        chordDurations.push(chordDuration)
        // 上一个音符
        let previousMelody: any;
        let previousChord: any;
        // 上上一个音符
        let precedingMelody: any;
        let precedingChord: any;
        // 生成的关系
        let relation: any;
        let _note: any;
        // 当前音符对应的十二进制值
        const melodyMathVal = noteTo12(key, accidentals);
        if (i === 0 && n === 0) {
          // 第一个旋律的音程是纯八、五度、大三或者小三 (暂时先不考虑小三)
          const _rst = generate358IntervalNote(baseNote)
          _note = _rst._note
          relation = _rst.relation

          chordStructs[i].push(_note)
          relations.push(relation)
          console.log(`【小节:0;音符顺序:0】 按照音程关系${relation}更新二声部骨架${_note}`)
          return;
        }
        // 找到上一个和上上一个音符
        let currentNoteIndex = n
        let currentBarIndex = i
        let _rst: any = findPreviousTwoNote(currentNoteIndex, currentBarIndex, melodyStaves, chordStructs, currentBarMelodyNum)
        previousMelody = _rst.previousMelody
        precedingMelody = _rst.precedingMelody
        previousChord = _rst.previousChord
        precedingChord = _rst.precedingChord

        // 优先级进，并尽量适配3、6度音程
        _rst = generateStepNote({section_index: i, note_index: n}, previousMelody, previousChord, baseNote)
        _note = _rst._note
        relation = _rst.relation

        // 判断平行3/6度，同音反复
        const parallelAllow = checkParallel(currentNoteIndex, currentBarIndex, melodyStaves, chordStructs, baseNote, _note)

        if (!parallelAllow) {
          // 平行三六度、同音反复检测失败，插入5/8度音程
          _rst = generate58IntervalNote({section_index: i, note_index: n}, baseNote)
          _note = _rst._note
          relation = _rst.relation
        }
        // 判断是否大跳
        _rst = handleJumping({section_index: i, note_index: n}, previousChord, precedingChord, baseNote, _note)
        _note = _rst._note
        relation = _rst.relation || relation

        if (previousMelody && previousChord) {
          // 避免平行八度：
          _rst = handleParallelOctave({section_index: i, note_index: n}, previousMelody, previousChord, baseNote, _note)
          _note = _rst._note
          relation = _rst.relation || relation
          // 避免平行五度：
          _rst = handleParallelFifth({section_index: i, note_index: n}, previousMelody, previousChord, baseNote, _note)
          _note = _rst._note
          relation = _rst.relation || relation
          // 避免隐伏五八度
          _rst = handleHiddenOctaveFifth({section_index: i, note_index: n}, previousMelody, previousChord, baseNote, _note)
          _note = _rst._note
          relation = _rst.relation || relation
        }

        // 更新最终的relation和note
        relations.push(relation)
        chordStructs[i].push(_note)
      })
    })

    relations = relations.map(relation => Interval.simplify(relation))
    // # 和 b 互相转化
    // chordStructs = chordStructs.map(notes => {
    //   return notes.map((note: string) => Note.enharmonic(Note.simplify(note)))
    // })


    // 转化一层
    let _i = 0
    const rst: Array<any> = []
    chordStructs.forEach((notes: Array<string>, charIndex: number) => {
      rst[charIndex] = []
      notes.forEach((note: string, index: number) => {
        rst[charIndex].push({
          notes: [note],
          relation: relations[_i],
          duration: chordDurations[_i],
          baseNote: baseNotes[charIndex][index]
        })
        _i++
      })
    })

    // 渲染界面
    // const chordRst = staveDataTransfer(rst)
    // const scoreString = localStorage.getItem(SCORE_STORE_KEY);
    // const score = scoreString ? JSON.parse(scoreString) : undefined
    // score.chordStaves = chordRst
    // this.restoreScore(score)
    // this.redraw();

    // >>>>>>此处已经确定了二声部的骨架，填充和弦<<<<<<<<
    this.chordMatching(rst)
  }

  public chordMatching = (chordRst: Array<any>) => {
    let chordStaves: Array<any> = []
    chordRst.forEach((chords, charIndex) => {
      chordStaves[charIndex] = []
      chords.forEach((chord: any, n: number) => {
        chordStaves[charIndex][n] = []
        // 旋律音
        const baseNote = chord.baseNote
        // 根音
        let rootNote = chord.notes[0]
        let secondNote = ''
        let thirdNote = ''
        // 与旋律部分间隔八度
        if (['8P', '-8P', '1P', '-1P'].indexOf(chord.relation) !== -1) {
          const randomInteger = Math.floor(Math.random() * 3) + 1
          // 原始和弦
          if (randomInteger === 1) {
            secondNote = transpose(rootNote, '3M')
            thirdNote = transpose(rootNote, '5P')
          }
          // 第一转位
          if (randomInteger === 2) {
            secondNote = transpose(rootNote, '3M')
            thirdNote = transpose(rootNote, '6M')
          }
          // 第二转位
          if (randomInteger === 3) {
            secondNote = transpose(rootNote, '4P')
            thirdNote = transpose(rootNote, '6M')
          }
        }

        // 与旋律部分间隔三度
        if (['3M', '-3M', '3m', '-3m'].indexOf(chord.relation) !== -1) {
          const randomInteger = Math.floor(Math.random() * 2) + 1
          // 原始和弦
          if (randomInteger === 1) {
            secondNote = transpose(rootNote, '3M')
            thirdNote = transpose(rootNote, '5P')
          }
          // 第一转位
          if (randomInteger === 2) {
            secondNote = transpose(rootNote, '3M')
            thirdNote = transpose(rootNote, '6M')
          }
        }

        // 与旋律部分间隔六度
        if (['6M', '-6M', '6m', '-6m'].indexOf(chord.relation) !== -1) {
          const randomInteger = Math.floor(Math.random() * 2) + 1
          // 第一转位
          if (randomInteger === 1) {
            secondNote = transpose(rootNote, '3M')
            thirdNote = transpose(rootNote, '6M')
          }
          // 第二转位
          if (randomInteger === 2) {
            secondNote = transpose(rootNote, '4P')
            thirdNote = transpose(rootNote, '6M')
          }
        }

        // 与旋律部分间隔五度
        if (['5P', '-5P', '5d', '-5d'].indexOf(chord.relation) !== -1) {
          // 仅有原位
          secondNote = transpose(rootNote, '3M')
          thirdNote = transpose(rootNote, '5P')
        }

        // 与旋律部分间隔四度
        if (['4P', '-4P'].indexOf(chord.relation) !== -1) {
          // 仅有第二转位
          secondNote = transpose(rootNote, '4P')
          thirdNote = transpose(rootNote, '6M')
        }

        if (!secondNote || !thirdNote) {
          secondNote = transpose(rootNote, '3M')
          thirdNote = transpose(rootNote, '5P')
        }

        // 判断是否要拿掉增降音符号(先把弦内音所有的升降号都拿掉)
        const _secondNote = Note.get(secondNote)
        const _thirdNote = Note.get(thirdNote)
        if (!_secondNote.empty) {
          secondNote = _secondNote.letter + _secondNote.oct
        }
        if (!_thirdNote.empty) {
          thirdNote = _thirdNote.letter + _thirdNote.oct
        }
        // 去掉类似 ## 与 bb 等
        secondNote = Note.simplify(secondNote)
        thirdNote = Note.simplify(thirdNote)

        // 处理声部超越的问题
        let octInterval = 0
        const thirdNoteDistance = Interval.get(distance(thirdNote, baseNote))
        if (thirdNoteDistance.dir === -1) {
          // 发生了声部超越的问题
          const semitones = thirdNoteDistance.semitones || 0
          octInterval = Math.floor(Math.abs(semitones) / 12) + 1
        } else {
          if (thirdNoteDistance.semitones === 0) {
            // 同音
            octInterval = 1
          }
        }

        if (octInterval > 0) {
          // 整体向下移动
          rootNote = Note.transpose(rootNote, `-${octInterval * 8}P`)
          secondNote = Note.transpose(secondNote, `-${octInterval * 8}P`)
          thirdNote = Note.transpose(thirdNote, `-${octInterval * 8}P`)
          console.log(`【小节:${charIndex};音符顺序:${n}】 避免声部超越，向下平移${octInterval}个八度`)
        }


        chordStaves[charIndex][n] = {
          notes: [rootNote, secondNote, thirdNote],
          relation: chord.relation,
          duration: chord.duration
        }
      })
    })
    // console.log(chordStaves)
    chordStaves = staveDataTransfer(chordStaves)
    const scoreString = localStorage.getItem(SCORE_STORE_KEY);
    const score = scoreString ? JSON.parse(scoreString) : undefined
    score.chordStaves = chordStaves
    this.restoreScore(score)
    this.redraw();
  }
}
